import { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Card — a panel, and deliberately not a feed card.
 *
 * Two different objects share this word in HOTTR and confusing them is how the
 * estate ended up with boxes everywhere:
 *
 *   FEED CARD (Screen System §7) is not a box at all. Cards in a feed are
 *   separated by a white/5 hairline and nothing else — no border, no fill, no
 *   radius. The only bordered thing is their media. That is `MediaCard`.
 *
 *   PANEL — this component — is a surface that groups controls or figures on a
 *   settings, console or statistics screen. It gets charcoal, a hairline and a
 *   2px corner.
 *
 * If you are rendering a list of content objects, you want the feed grammar and
 * not this.
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 *
 * Stock shadcn's Card, which four apps shipped byte-identically:
 *
 *   rounded-lg border bg-card text-card-foreground shadow-sm
 *
 * Three brand violations in one line — an 8px radius where HOTTR is 2px, a
 * black drop shadow where the only legal shadow is a pink bloom, and a full
 * border where the brand uses a hairline. And `CardTitle` was the worst of it:
 * `text-2xl font-semibold tracking-tight` is 600 weight, sentence case and
 * -0.025em against a brand that is 700, uppercase and -0.02em. It was the
 * single most visible typographic difference between HOTTR apps.
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-sm border border-white/[0.05] bg-hottr-charcoal text-white',
        // No shadow. The Orb owns the glow; a panel that blooms competes with it.
        className,
      )}
      {...rest}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 p-5', className)} {...rest} />
  ),
);
CardHeader.displayName = 'CardHeader';

/**
 * H3 in the twelve voices — 700 / 16px / -0.02em / uppercase.
 *
 * Rendered as a `div` rather than an `h3`: a panel heading is rarely the right
 * document outline level, and shadcn's original emitted `h3` everywhere, which
 * produced pages with nine peer headings and no h1. Pass `as` semantics at the
 * call site where the outline genuinely matters.
 */
export interface CardTitleProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The element to render when this panel title genuinely belongs in the
   * document outline — `as="h2"`. Defaults to `div`, which keeps it out.
   *
   * The ref stays typed as `HTMLDivElement` even when a heading is rendered.
   * That is a deliberate imprecision: it keeps the diff small and preserves
   * exact ref and event-handler typing for the default case, which is the
   * common one.
   */
  as?: ElementType;
}

export const CardTitle = forwardRef<HTMLDivElement, CardTitleProps>(
  ({ as: Tag = 'div', className, ...rest }, ref) => (
    <Tag ref={ref} className={cn('hottr-h3', className)} {...rest} />
  ),
);
CardTitle.displayName = 'CardTitle';

/** Body sm — 400 / 13px, white at 55%. Never a grey. */
export const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cn('text-[13px] leading-[1.5] text-white/55', className)} {...rest} />
  ),
);
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cn('p-5 pt-0', className)} {...rest} />
  ),
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3 p-5 pt-0', className)} {...rest} />
  ),
);
CardFooter.displayName = 'CardFooter';
